import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import {
  Building2, Users, Receipt, Globe, LogOut, Activity, ArrowLeft, Palette, ListChecks,
  Shield, KeyRound, BookOpen, CalendarRange, ScrollText,
} from 'lucide-react';
import { hasFullAccess, hasPermission, isTenantAdmin } from '@/utils/roleAccess';
import { ADMIN_NAV_PERMISSIONS } from '@/config/navigationMenus';
import { homeForRole } from '@/utils/homeForRole';
import { IITH } from '@/pages/landing/iithBrand';
import { IITH_LOGO_URL } from '@/config/iithAssets';

const NAV = [
  ['/admin/overview', 'Overview', Activity, 'ta-nav-overview'],
  ['/admin/users', 'Users', Users, 'ta-nav-users'],
  ['/admin/roles', 'Roles', Shield, 'ta-nav-roles'],
  ['/admin/permissions', 'Permissions', KeyRound, 'ta-nav-permissions'],
  ['/admin/specialisations', 'Specialisations', BookOpen, 'ta-nav-specialisations'],
  ['/admin/roster', 'Roster planner', CalendarRange, 'ta-nav-roster'],
  ['/admin/auth-audit', 'Auth audit', ScrollText, 'ta-nav-auth-audit'],
  ['/admin/billing', 'Billing & Plan', Receipt, 'ta-nav-billing'],
  ['/admin/branding', 'Branding', Palette, 'ta-nav-branding'],
  ['/admin/capabilities', 'Capabilities', ListChecks, 'ta-nav-capabilities'],
  ['/admin/domains', 'Custom Domains', Globe, 'ta-nav-domains'],
  ['/admin/settings', 'Clinic Info', Building2, 'ta-nav-settings'],
];

const SAAS_ADMIN_ONLY = new Set([
  '/admin/billing', '/admin/branding', '/admin/capabilities',
  '/admin/domains', '/admin/settings',
]);

function navVisible(user, path) {
  if (SAAS_ADMIN_ONLY.has(path)) {
    const r = (user?.role || '').toLowerCase();
    return r === 'admin' || hasFullAccess(user);
  }
  const need = ADMIN_NAV_PERMISSIONS[path];
  if (need == null) return true;
  return hasFullAccess(user) || hasPermission(user, need);
}

export default function TenantAdminLayout() {
  const { user, logout } = useAuth();
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const brand = tenant?.branding?.primary_color || IITH.saffron;
  const logo = tenant?.branding?.logo_url || IITH_LOGO_URL;
  const clinic = tenant?.branding?.display_name || tenant?.name || IITH.hospital;

  useEffect(() => {
    if (!user) { navigate('/login', { replace: true }); return; }
    if (!isTenantAdmin(user)) {
      navigate(homeForRole(user.role, user), { replace: true }); return;
    }
    setReady(true);
  }, [user, navigate]);

  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <p className="text-sm text-zinc-500">Loading workspace…</p>
      </div>
    );
  }

  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition ${
      isActive
        ? 'text-white shadow-sm'
        : 'text-zinc-600 hover:bg-[#E8EEF5]'
    }`;

  return (
    <div className="min-h-screen flex bg-[#E8EEF5]" data-testid="tenant-admin-layout">
      <aside className="w-64 border-r bg-white flex flex-col">
        <div className="px-5 py-5 border-b" style={{ background: `linear-gradient(145deg, ${IITH.navy}, ${IITH.navyMid})` }}>
          <div className="flex items-center gap-2.5">
            <img src={logo} alt="" className="h-10 w-10 rounded-full bg-white p-1 object-contain" />
            <div className="min-w-0">
              <span className="font-semibold text-white block truncate font-heading text-sm">
                {clinic}
              </span>
              <p className="text-[10px] uppercase tracking-[0.16em] mt-0.5" style={{ color: IITH.gold }}>
                Clinic admin
              </p>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1" data-testid="ta-permission-nav">
          {NAV.filter(([to]) => navVisible(user, to)).map(([to, label, Icon, testid]) => (
            <NavLink
              key={to}
              to={to}
              className={linkClass}
              style={({ isActive }) => (isActive ? { background: brand } : undefined)}
              data-testid={testid}
            >
              <Icon className="h-4 w-4" /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t space-y-1">
          <button
            onClick={() => navigate(homeForRole(user?.role, user))}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-white rounded-lg hover:brightness-110"
            style={{ background: IITH.navy }}
            data-testid="ta-back-to-emr"
          >
            <ArrowLeft className="h-4 w-4" />
            {(user?.role || '').toLowerCase() === 'management' ? 'Back to Management' : 'Open EMR · Appointments'}
          </button>
          {(hasFullAccess(user) || (user?.role || '').toLowerCase() === 'management') && (
            <div className="grid grid-cols-2 gap-1 pt-1">
              {[
                ['/management', 'Management'],
                ['/reception/home', 'Reception'],
                ['/reception/roster', 'Roster'],
                ['/pharmacy', 'Pharmacy'],
              ].map(([to, label]) => (
                <button
                  key={to}
                  type="button"
                  onClick={() => navigate(to)}
                  className="px-2 py-1.5 text-[11px] font-medium text-zinc-700 bg-zinc-50 hover:bg-zinc-100 border rounded-md"
                >
                  {label}
                </button>
              ))}
            </div>
          )}
          <div className="text-[11px] text-zinc-500 pt-2 px-1">Signed in as</div>
          <div className="text-sm font-medium text-zinc-800 truncate px-1">{user?.email}</div>
          <button
            onClick={() => { logout(); }}
            className="mt-1 w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-zinc-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition"
            data-testid="ta-logout-btn"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 overflow-y-auto">
        <Outlet />
      </main>
    </div>
  );
}
